<template>
  <v-container fluid class="refill-new">
    <avs-card>
      <template v-slot:title>
        <div class="d-flex align-center" style="width:100%; gap:12px;">
          <v-btn icon small @click="$router.push({ name: 'RefillRequestList' })">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <div class="text-h6 font-weight-bold">
              <v-icon left color="primary">mdi-package-variant-plus</v-icon>
              {{ $t("Nouvelle demande de livraison") }}
            </div>
            <div class="text-caption grey--text">
              {{ $t("Indiquez le produit, la quantité, la date souhaitée et joignez le bon de commande.") }}
            </div>
          </div>
        </div>
      </template>

      <template v-slot:content>
        <v-form ref="form" v-model="formValid" @submit.prevent="submit" class="pa-4">
          <v-alert
            v-if="showInfoBanner"
            type="info"
            text
            dismissible
            border="left"
            colored-border
            elevation="0"
            class="mb-4"
            @input="dismissInfoBanner"
          >
            <div class="font-weight-bold mb-1">A quoi sert ce formulaire ?</div>
            <div class="text-body-2">
              Utilisez ce formulaire pour demander un rechargement de carburant
              aupres de notre partenaire. Indiquez le produit, la quantite et
              joignez le scan de votre bon de commande physique
              (image ou PDF, max 20 Mo). Vous suivrez l avancement dans la liste
              de vos demandes.
            </div>
          </v-alert>

          <v-row>
            <!-- Societe principale (auto-selectionnee, read-only) -->
            <v-col cols="12">
              <v-text-field
                :value="principalName || $t('Aucune société principale configurée')"
                :label="$t('Société principale')"
                prepend-inner-icon="mdi-domain"
                outlined
                dense
                readonly
                :error="!principalName"
                :error-messages="!principalName ? [$t('Aucune société principale n\'est disponible.')] : []"
              />
            </v-col>

            <!-- Produit -->
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="form.product"
                :items="products"
                item-text="name"
                item-value="id"
                :label="$t('Produit')"
                :rules="[v => !!v || $t('Le produit est requis')]"
                :loading="loadingProducts"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-gas-station"
              />
            </v-col>

            <!-- Quantite -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.quantity"
                :label="$t('Quantité (litres)')"
                type="number"
                step="0.01"
                min="0.01"
                :rules="quantityRules"
                outlined
                dense
                prepend-inner-icon="mdi-numeric"
              />
            </v-col>

            <!-- Date livraison souhaitee + Bon de commande sur la meme ligne -->
            <v-col cols="12" md="6">
              <v-menu
                v-model="deliveryMenu"
                :close-on-content-click="false"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.desired_delivery_date"
                    :label="$t('Date de livraison souhaitée')"
                    :rules="deliveryDateRules"
                    prepend-inner-icon="mdi-calendar-clock"
                    outlined
                    dense
                    readonly
                    clearable
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="form.desired_delivery_date"
                  :min="todayStr"
                  @input="deliveryMenu = false"
                />
              </v-menu>
            </v-col>

            <!-- Bon de commande -->
            <v-col cols="12" md="6">
              <v-file-input
                v-model="form.order_document"
                :label="$t('Bon de commande (image ou PDF, max 20 Mo)')"
                :rules="documentRules"
                accept="image/*,application/pdf"
                outlined
                dense
                show-size
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                :hint="$t('Scannez ou prenez une photo du bon de commande physique. Taille max : 20 Mo.')"
                persistent-hint
              />
              <div v-if="previewUrl" class="mt-3">
                <img :src="previewUrl" alt="aperçu" class="preview-img" />
              </div>
            </v-col>

            <!-- Site de livraison (combobox : valeurs deja saisies + saisie libre) -->
            <v-col cols="12" md="6">
              <v-combobox
                v-model="form.delivery_shop"
                :items="deliveryShopOptions"
                :search-input.sync="deliveryShopSearch"
                :label="$t('Site de livraison (optionnel)')"
                :loading="loadingShops"
                :hint="$t('Choisissez un site deja utilise ou saisissez-en un nouveau')"
                persistent-hint
                outlined
                dense
                clearable
                hide-no-data
                prepend-inner-icon="mdi-map-marker"
                @blur="commitDeliveryShop"
                @keyup.enter="commitDeliveryShop"
              />
            </v-col>

            <!-- Destination -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.destination"
                :label="$t('Destination (optionnel)')"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker-radius"
              />
            </v-col>

            <!-- Notes -->
            <v-col cols="12">
              <v-textarea
                v-model="form.notes"
                :label="$t('Notes (optionnel)')"
                outlined
                dense
                rows="3"
              />
            </v-col>
          </v-row>

          <div class="d-flex justify-end" style="gap: 8px;">
            <v-btn text @click="$router.push({ name: 'RefillRequestList' })">
              {{ $t("Annuler") }}
            </v-btn>
            <v-btn
              type="submit"
              color="primary"
              depressed
              :loading="submitting"
              :disabled="!formValid || !principalName"
            >
              <v-icon left>mdi-send</v-icon>
              {{ $t("Envoyer la demande") }}
            </v-btn>
          </div>
        </v-form>
      </template>
    </avs-card>
  </v-container>
</template>

<script>
import { RefillRequestService, ProductDetailService, CompanyService } from "@/services"

export default {
  name: "RefillRequestNew",

  data() {
    return {
      formValid: false,
      submitting: false,
      loadingProducts: false,
      loadingShops: false,

      principalName: "",
      products: [],
      deliveryShopOptions: [],
      deliveryShopSearch: "",

      form: {
        product: null,
        quantity: null,
        desired_delivery_date: null,
        delivery_shop: "",
        destination: "",
        order_document: null,
        notes: "",
      },

      deliveryMenu: false,
      todayStr: new Date().toISOString().slice(0, 10),
      previewUrl: null,
      showInfoBanner: !localStorage.getItem("refill_new_info_dismissed"),
      MAX_DOC_SIZE: 20 * 1024 * 1024,
    }
  },

  computed: {
    quantityRules() {
      return [
        v => v != null && v !== "" || this.$t("La quantité est requise"),
        v => Number(v) > 0 || this.$t("La quantité doit être positive"),
      ]
    },
    documentRules() {
      return [
        v => !!v || this.$t("Le bon de commande est requis"),
        v => !v || v.size <= this.MAX_DOC_SIZE
          || this.$t("Le fichier dépasse 20 Mo (envoi par email impossible)"),
      ]
    },
    deliveryDateRules() {
      return [
        v => !!v || this.$t("La date de livraison souhaitée est requise"),
        v => !v || v >= this.todayStr
          || this.$t("La date doit être aujourd'hui ou dans le futur"),
      ]
    },
  },

  watch: {
    "form.order_document"(file) {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = null
      }
      if (file && file.type && file.type.startsWith("image/")) {
        this.previewUrl = URL.createObjectURL(file)
      }
    },
  },

  created() {
    this.fetchPrincipal()
    this.fetchProducts()
    this.fetchShops()
  },

  beforeDestroy() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
  },

  methods: {
    dismissInfoBanner() {
      try {
        localStorage.setItem("refill_new_info_dismissed", "1")
      } catch (e) {
        // ignore
      }
      this.showInfoBanner = false
    },

    async fetchPrincipal() {
      try {
        const list = await CompanyService.getAll()
        const principal = (list || []).find(c => c.is_principal)
        this.principalName = principal ? principal.name : ""
      } catch (e) {
        this.principalName = ""
      }
    },

    async fetchProducts() {
      this.loadingProducts = true
      try {
        const r = await ProductDetailService.getprincipalAll()
        this.products = r.results || r || []
      } catch (e) {
        this.products = []
      } finally {
        this.loadingProducts = false
      }
    },

    async fetchShops() {
      // Recupere la liste des sites de livraison deja saisis par les
      // utilisateurs de la societe courante (combobox auto-complete).
      this.loadingShops = true
      try {
        const r = await RefillRequestService.getDeliveryShops()
        this.deliveryShopOptions = Array.isArray(r) ? r : []
      } catch (e) {
        this.deliveryShopOptions = []
      } finally {
        this.loadingShops = false
      }
    },

    // v-combobox de Vuetify 2 ne commite pas la valeur tapee tant qu'on n'a
    // pas presse Entree OU quitte le champ. On force le commit ici pour que
    // le texte saisi soit toujours dans form.delivery_shop avant submit.
    commitDeliveryShop() {
      const v = (this.deliveryShopSearch || "").trim()
      if (v && this.form.delivery_shop !== v) {
        this.form.delivery_shop = v
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return
      // Force le commit du combobox au cas ou l'utilisateur n'a pas blur/Entree
      this.commitDeliveryShop()
      // v-combobox peut renvoyer un objet si items est un tableau d'objets ;
      // ici les items sont des strings, mais on securise quand meme.
      const ds = this.form.delivery_shop
      const deliveryShopStr = (typeof ds === "string")
        ? ds.trim()
        : (ds && ds.text ? String(ds.text).trim() : "")
      this.submitting = true
      try {
        await RefillRequestService.create({
          product: this.form.product,
          quantity: this.form.quantity,
          desired_delivery_date: this.form.desired_delivery_date,
          delivery_shop: deliveryShopStr,
          destination: this.form.destination,
          notes: this.form.notes,
          order_document: this.form.order_document,
        })
        this.$store.commit("app/message", {
          msg: this.$t("Demande envoyée avec succès"),
          color: "success",
        })
        this.$router.push({ name: "RefillRequestList" })
      } catch (err) {
        const data = err?.response?.data || {}
        const msg = data.detail
          || data.quantity?.[0]
          || data.order_document?.[0]
          || data.product?.[0]
          || this.$t("Échec de l'envoi de la demande")
        this.$store.commit("app/message", { msg, color: "error" })
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.preview-img {
  max-width: 100%;
  max-height: 280px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}
</style>
